import useLocalStorage from './useLocalStorage'

import axios from 'axios'

import type { MerchantTexts } from '@/types/types'
import type { LoyaltyMerchantFormTexts } from '@/types/global'

const apiLanguageId = import.meta.env.VITE_API_LANGUAGE_ID

const merchantTextsForm = ref<LoyaltyMerchantFormTexts>(null)
const isPolite = ref(false)

export default function useMerchantTexts() {
  const { user } = useLocalStorage()
  const notification = useNotification()
  const { t } = useI18n()

  const getMerchantTexts = async (): Promise<MerchantTexts> => {
    try {
      const { data } = await axios.get(`/merchants/${user.value.merchant.id}/texts/${apiLanguageId}`)
      isPolite.value = data.data.usePoliteLanguage
      return data.data
    } catch {
      notification.create('error', {
        text: t('pages.settings.merchantForm.notifications.get.merchantTextsError'),
        timeout: 3000
      })
    }
  }

  const webappTextDefaults = (isFormal: boolean) => {
    const tone = isFormal ? 'valueFormal' : 'value'

    return {
      webappHomeTitle: t(`pages.settings.merchantForm.text.webappTextsFields.webappHomeTitle.${tone}`),
      webappHomeSubtitle: t(`pages.settings.merchantForm.text.webappTextsFields.webappHomeSubtitle.${tone}`)
    }
  }

  const createWebappTextsFields = (isFormal: boolean) => {
    const defaults = webappTextDefaults(isFormal)

    return {
      webappHomeTitle: {
        label: t('pages.settings.merchantForm.text.webappTextsFields.webappHomeTitle.label'),
        value: defaults.webappHomeTitle,
        type: 'text',
        rules: 'required',
        maxLength: 75
      },
      webappHomeSubtitle: {
        label: t('pages.settings.merchantForm.text.webappTextsFields.webappHomeSubtitle.label'),
        value: defaults.webappHomeSubtitle,
        type: 'text',
        rules: 'required',
        maxLength: 30
      }
    }
  }

  // Prefilled with the webapp's default text for the current tone. Saved empty while it still holds
  // that default, so the webapp keeps showing it translated in the customer's language.
  const createWebappRegistrationTextsFields = (isFormal: boolean) => {
    const field = (key: string, maxLength: number) => {
      const defaultValue = t(
        `pages.loyaltySupports.webapp.registrationTexts.${key}.${isFormal ? 'defaultFormal' : 'default'}`
      )

      return {
        label: t(`pages.loyaltySupports.webapp.registrationTexts.${key}.label`),
        value: defaultValue,
        defaultValue,
        type: 'text',
        rules: '',
        maxLength
      }
    }

    return {
      webappStoreSelectionTitle: field('webappStoreSelectionTitle', 75),
      webappStoreSelectionLabel: field('webappStoreSelectionLabel', 120),
      webappProfileTitle: field('webappProfileTitle', 75),
      webappProfileSubtitle: field('webappProfileSubtitle', 120),
      webappWelcomeButton: field('webappWelcomeButton', 30),
      webappPhoneSubtitle: field('webappPhoneSubtitle', 120),
      webappOptinTitle: field('webappOptinTitle', 75),
      webappOptinSubtitle: field('webappOptinSubtitle', 120),
      webappOptinYes: field('webappOptinYes', 30),
      webappOptinNo: field('webappOptinNo', 30)
    }
  }

  const formalDefaultTexts = {
    homeTextsFields: {
      homeTitle: {
        label: t('pages.settings.merchantForm.text.homeTextsFields.homeTitle.label'),
        value: t('pages.settings.merchantForm.text.homeTextsFields.homeTitle.valueFormal'),
        type: 'text',
        rules: 'required'
      },
      homeSubtitle: {
        label: t('pages.settings.merchantForm.text.homeTextsFields.homeSubtitle.label'),
        value: t('pages.settings.merchantForm.text.homeTextsFields.homeSubtitle.valueFormal', {
          merchant: localStorage.merchantName
        }),
        type: 'text',
        rules: 'required'
      },
      homeButton: {
        label: t('pages.settings.merchantForm.text.homeTextsFields.homeButton.label'),
        value: t('pages.settings.merchantForm.text.homeTextsFields.homeButton.valueFormal'),
        type: 'text',
        rules: 'required',
        maxLength: 22
      },
      homeButtonWithActiveIntegration: {
        label: t('pages.settings.merchantForm.text.homeTextsFields.homeButtonWithActiveIntegration.label'),
        value: t('pages.settings.merchantForm.text.homeTextsFields.homeButtonWithActiveIntegration.valueFormal'),
        type: 'text',
        rules: 'required',
        maxLength: 22
      }
    },
    loginTextsFields: {
      loginTitle: {
        label: t('pages.settings.merchantForm.text.loginTextsFields.loginTitle.label'),
        value: t('pages.settings.merchantForm.text.loginTextsFields.loginTitle.valueFormal'),
        type: 'text',
        rules: 'required'
      },
      loginSubtitle: {
        label: t('pages.settings.merchantForm.text.loginTextsFields.loginSubtitle.label'),
        value: t('pages.settings.merchantForm.text.loginTextsFields.loginSubtitle.valueFormal'),
        type: 'text',
        rules: 'required'
      }
    },
    pointsTextsFields: {
      pointsTitle: {
        label: t('pages.settings.merchantForm.text.pointsTextsFields.pointsTitle.label'),
        value: t('pages.settings.merchantForm.text.pointsTextsFields.pointsTitle.valueFormal'),
        type: 'text',
        rules: 'required'
      },
      pointsSubtitle: {
        label: t('pages.settings.merchantForm.text.pointsTextsFields.pointsSubtitle.label'),
        value: t('pages.settings.merchantForm.text.pointsTextsFields.pointsSubtitle.valueFormal'),
        type: 'text',
        rules: 'required'
      }
    },
    orderTextsFields: {
      integrationPointsClaimingTitle: {
        label: t('pages.settings.merchantForm.text.orderTextsFields.integrationPointsClaimingTitle.label'),
        value: t('pages.settings.merchantForm.text.orderTextsFields.integrationPointsClaimingTitle.valueFormal'),
        type: 'text',
        rules: 'required'
      },
      integrationPointsClaimingSubtitle: {
        label: t('pages.settings.merchantForm.text.orderTextsFields.integrationPointsClaimingSubtitle.label'),
        value: t('pages.settings.merchantForm.text.orderTextsFields.integrationPointsClaimingSubtitle.valueFormal'),
        type: 'text',
        rules: 'required'
      },
      integrationFailedTitle: {
        label: t('pages.settings.merchantForm.text.orderTextsFields.integrationFailedTitle.label'),
        value: t('pages.settings.merchantForm.text.orderTextsFields.integrationFailedTitle.valueFormal'),
        type: 'text',
        rules: 'required'
      },
      integrationFailedSubtitle: {
        label: t('pages.settings.merchantForm.text.orderTextsFields.integrationFailedSubtitle.label'),
        value: t('pages.settings.merchantForm.text.orderTextsFields.integrationFailedSubtitle.valueFormal'),
        type: 'text',
        rules: 'required'
      }
    },
    rewardsTextsFields: {
      rewardsTitle: {
        label: t('pages.settings.merchantForm.text.rewardsTextsFields.rewardsTitle.label'),
        value: t('pages.settings.merchantForm.text.rewardsTextsFields.rewardsTitle.valueFormal'),
        type: 'text',
        rules: 'required'
      },
      rewardsSubtitle: {
        label: t('pages.settings.merchantForm.text.rewardsTextsFields.rewardsSubtitle.label'),
        value: t('pages.settings.merchantForm.text.rewardsTextsFields.rewardsSubtitle.valueFormal'),
        type: 'text',
        rules: 'required'
      },
      rewardsSubtitleNew: {
        label: t('pages.settings.merchantForm.text.rewardsTextsFields.rewardsSubtitleNew.label'),
        value: t('pages.settings.merchantForm.text.rewardsTextsFields.rewardsSubtitleNew.valueFormal'),
        type: 'text',
        rules: 'required'
      },
      rewardsText: {
        label: t('pages.settings.merchantForm.text.rewardsTextsFields.rewardsText.label'),
        value: t('pages.settings.merchantForm.text.rewardsTextsFields.rewardsText.valueFormal'),
        type: 'text',
        rules: 'required'
      },
      rewardsButton: {
        label: t('pages.settings.merchantForm.text.rewardsTextsFields.rewardsButton.label'),
        value: t('pages.settings.merchantForm.text.rewardsTextsFields.rewardsButton.valueFormal'),
        type: 'text',
        rules: 'required'
      },
      rewardsPopupConfirmTitle: {
        label: t('pages.settings.merchantForm.text.rewardsTextsFields.rewardsPopupConfirmTitle.label'),
        value: t('pages.settings.merchantForm.text.rewardsTextsFields.rewardsPopupConfirmTitle.valueFormal'),
        type: 'text',
        rules: 'required'
      },
      rewardsPopupConfirmSubtitle: {
        label: t('pages.settings.merchantForm.text.rewardsTextsFields.rewardsPopupConfirmSubtitle.label'),
        value: `${t(
          'pages.settings.merchantForm.text.rewardsTextsFields.rewardsPopupConfirmSubtitle.valueFormal1'
        )} {{points}} ${t(
          'pages.settings.merchantForm.text.rewardsTextsFields.rewardsPopupConfirmSubtitle.value2'
        )} {{reward}} ?`,
        type: 'text',
        rules: 'required'
      },
      name: {
        label: t('pages.settings.merchantForm.text.rewardsTextsFields.name.label'),
        value: t('pages.settings.merchantForm.text.rewardsTextsFields.name.valueFormal'),
        type: 'text',
        rules: 'required'
      },
      rewardsPopupForbiddenTitle: {
        label: t('pages.settings.merchantForm.text.rewardsTextsFields.rewardsPopupForbiddenTitle.label'),
        value: t('pages.settings.merchantForm.text.rewardsTextsFields.rewardsPopupForbiddenTitle.valueFormal'),
        type: 'text',
        rules: 'required'
      }
    },
    rewardWonTextsFields: {
      congratsTitle: {
        label: t('pages.settings.merchantForm.text.rewardWonTextsFields.congratsTitle.label'),
        value: t('pages.settings.merchantForm.text.rewardWonTextsFields.congratsTitle.valueFormal'),
        type: 'text',
        rules: 'required'
      },
      congratsSubtitle: {
        label: t('pages.settings.merchantForm.text.rewardWonTextsFields.congratsSubtitle.label'),
        value: t('pages.settings.merchantForm.text.rewardWonTextsFields.congratsSubtitle.valueFormal'),
        type: 'text',
        rules: 'required'
      },
      congratsSubtitleSms: {
        label: t('pages.settings.merchantForm.text.rewardWonTextsFields.congratsSubtitleSms.label'),
        value: t('pages.settings.merchantForm.text.rewardWonTextsFields.congratsSubtitleSms.valueFormal'),
        type: 'text',
        rules: 'required'
      }
    },
    orderReceivedTextsFields: {
      integrationAfterOrderMailSubject: {
        label: t('pages.settings.merchantForm.text.orderReceivedTextsFields.integrationAfterOrderMailSubject.label'),
        value: '',
        type: 'text',
        rules: 'required'
      },
      integrationAfterOrderMailBody: {
        label: t('pages.settings.merchantForm.text.integrationAfterOrderMailBody.integrationAfterOrderMailBody.label'),
        value: '',
        type: 'text',
        rules: 'required'
      },
      integrationAfterOrderSms: {
        label: t('pages.settings.merchantForm.text.orderReceivedTextsFields.integrationAfterOrderSms.label'),
        value: '',
        type: 'text',
        rules: 'required'
      }
    },
    questionsTextsFields: {
      questionsOptin: {
        label: t('pages.settings.merchantForm.text.questionsTextsFields.questionsOptin.label'),
        value: t('pages.settings.merchantForm.text.questionsTextsFields.questionsOptin.valueFormal'),
        type: 'text',
        rules: 'required'
      },
      questionsOptinYes: {
        label: t('pages.settings.merchantForm.text.questionsTextsFields.questionsOptinYes.label'),
        value: t('pages.settings.merchantForm.text.questionsTextsFields.questionsOptinYes.valueFormal'),
        type: 'text',
        rules: 'required'
      },
      questionsOptinNo: {
        label: t('pages.settings.merchantForm.text.questionsTextsFields.questionsOptinNo.label'),
        value: t('pages.settings.merchantForm.text.questionsTextsFields.questionsOptinNo.valueFormal'),
        type: 'text',
        rules: 'required'
      },
      questionsOptinEmail: {
        label: t('pages.settings.merchantForm.text.questionsTextsFields.questionsOptinEmail.label'),
        value: t('pages.settings.merchantForm.text.questionsTextsFields.questionsOptinEmail.valueFormal'),
        type: 'text',
        rules: 'required'
      },
      questionsIntro: {
        label: t('pages.settings.merchantForm.text.questionsTextsFields.questionsIntro.label'),
        value: t('pages.settings.merchantForm.text.questionsTextsFields.questionsIntro.valueFormal'),
        type: 'text',
        rules: 'required'
      },
      questionsIntroNew: {
        label: t('pages.settings.merchantForm.text.questionsTextsFields.questionsIntroNew.label'),
        value: t('pages.settings.merchantForm.text.questionsTextsFields.questionsIntroNew.valueFormal'),
        type: 'text',
        rules: 'required'
      },
      enrichmentQuestionBirthday: {
        label: t('pages.settings.merchantForm.text.questionsTextsFields.enrichmentQuestionBirthday.label'),
        value: t('pages.settings.merchantForm.text.questionsTextsFields.enrichmentQuestionBirthday.valueFormal'),
        type: 'text',
        rules: 'required'
      },
      enrichmentQuestionFirstname: {
        label: t('pages.settings.merchantForm.text.questionsTextsFields.enrichmentQuestionFirstname.label'),
        value: t('pages.settings.merchantForm.text.questionsTextsFields.enrichmentQuestionFirstname.valueFormal'),
        type: 'text',
        rules: 'required'
      },
      enrichmentQuestionGender: {
        label: t('pages.settings.merchantForm.text.questionsTextsFields.enrichmentQuestionGender.label'),
        value: t('pages.settings.merchantForm.text.questionsTextsFields.enrichmentQuestionGender.valueFormal'),
        type: 'text',
        rules: 'required'
      },
      enrichmentQuestionEmail: {
        label: t('pages.settings.merchantForm.text.questionsTextsFields.enrichmentQuestionEmail.label'),
        value: t('pages.settings.merchantForm.text.questionsTextsFields.enrichmentQuestionEmail.valueFormal'),
        type: 'text',
        rules: 'required'
      },
      questionsAnswered: {
        label: t('pages.settings.merchantForm.text.questionsTextsFields.questionsAnswered.label'),
        value: t('pages.settings.merchantForm.text.questionsTextsFields.questionsAnswered.valueFormal'),
        type: 'text',
        rules: 'required'
      },
      questionsNotAnswered: {
        label: t('pages.settings.merchantForm.text.questionsTextsFields.questionsNotAnswered.label'),
        value: t('pages.settings.merchantForm.text.questionsTextsFields.questionsNotAnswered.valueFormal'),
        type: 'text',
        rules: 'required'
      }
    },
    uniqueRewardTexts: {
      congratsSms: {
        label: t('pages.settings.merchantForm.text.rewardWonTextsFields.congratsSms.label'),
        value: t('pages.settings.merchantForm.text.rewardWonTextsFields.congratsSms.valueFormal'),
        type: 'text',
        rules: 'required'
      },
      voucherSms: {
        label: t('pages.settings.merchantForm.text.rewardWonTextsFields.voucherSms.label'),
        value: t('pages.settings.merchantForm.text.rewardWonTextsFields.voucherSms.valueFormal'),
        type: 'text',
        rules: 'required'
      },
      congratsSmsNew: {
        label: t('pages.settings.merchantForm.text.rewardWonTextsFields.congratsSmsNew.label'),
        value: t('pages.settings.merchantForm.text.rewardWonTextsFields.congratsSmsNew.valueFormal'),
        type: 'text',
        rules: 'required'
      }
    },
    languageTone: {
      usePoliteLanguage: {
        value: true
      }
    },
    webappTextsFields: createWebappTextsFields(true),
    webappRegistrationTextsFields: createWebappRegistrationTextsFields(true)
  }

  const nonFormalDefaultTexts = {
    homeTextsFields: {
      homeTitle: {
        label: t('pages.settings.merchantForm.text.homeTextsFields.homeTitle.label'),
        value: t('pages.settings.merchantForm.text.homeTextsFields.homeTitle.value'),
        type: 'text',
        rules: 'required'
      },
      homeSubtitle: {
        label: t('pages.settings.merchantForm.text.homeTextsFields.homeSubtitle.label'),
        value: t('pages.settings.merchantForm.text.homeTextsFields.homeSubtitle.value', {
          merchant: localStorage.merchantName
        }),
        type: 'text',
        rules: 'required'
      },
      homeButton: {
        label: t('pages.settings.merchantForm.text.homeTextsFields.homeButton.label'),
        value: t('pages.settings.merchantForm.text.homeTextsFields.homeButton.value'),
        type: 'text',
        rules: 'required',
        maxLength: 22
      },
      homeButtonWithActiveIntegration: {
        label: t('pages.settings.merchantForm.text.homeTextsFields.homeButtonWithActiveIntegration.label'),
        value: t('pages.settings.merchantForm.text.homeTextsFields.homeButtonWithActiveIntegration.value'),
        type: 'text',
        rules: 'required',
        maxLength: 22
      }
    },
    loginTextsFields: {
      loginTitle: {
        label: t('pages.settings.merchantForm.text.loginTextsFields.loginTitle.label'),
        value: t('pages.settings.merchantForm.text.loginTextsFields.loginTitle.value'),
        type: 'text',
        rules: 'required'
      },
      loginSubtitle: {
        label: t('pages.settings.merchantForm.text.loginTextsFields.loginSubtitle.label'),
        value: t('pages.settings.merchantForm.text.loginTextsFields.loginSubtitle.value'),
        type: 'text',
        rules: 'required'
      }
    },
    pointsTextsFields: {
      pointsTitle: {
        label: t('pages.settings.merchantForm.text.pointsTextsFields.pointsTitle.label'),
        value: t('pages.settings.merchantForm.text.pointsTextsFields.pointsTitle.value'),
        type: 'text',
        rules: 'required'
      },
      pointsSubtitle: {
        label: t('pages.settings.merchantForm.text.pointsTextsFields.pointsSubtitle.label'),
        value: t('pages.settings.merchantForm.text.pointsTextsFields.pointsSubtitle.value'),
        type: 'text',
        rules: 'required'
      },
      marketingBonusPointsTitle: {
        label: t('pages.settings.merchantForm.text.pointsTextsFields.marketingBonusPointsTitle.label'),
        value: t('pages.settings.merchantForm.text.pointsTextsFields.marketingBonusPointsTitle.value'),
        type: 'text',
        rules: 'required'
      },
      marketingBonusPointsSubtitle: {
        label: t('pages.settings.merchantForm.text.pointsTextsFields.marketingBonusPointsSubtitle.label'),
        value: t('pages.settings.merchantForm.text.pointsTextsFields.marketingBonusPointsSubtitle.value'),
        type: 'text',
        rules: 'required'
      }
    },
    orderTextsFields: {
      integrationPointsClaimingTitle: {
        label: t('pages.settings.merchantForm.text.orderTextsFields.integrationPointsClaimingTitle.label'),
        value: t('pages.settings.merchantForm.text.orderTextsFields.integrationPointsClaimingTitle.value'),
        type: 'text',
        rules: 'required'
      },
      integrationPointsClaimingSubtitle: {
        label: t('pages.settings.merchantForm.text.orderTextsFields.integrationPointsClaimingSubtitle.label'),
        value: t('pages.settings.merchantForm.text.orderTextsFields.integrationPointsClaimingSubtitle.value'),
        type: 'text',
        rules: 'required'
      },
      integrationFailedTitle: {
        label: t('pages.settings.merchantForm.text.orderTextsFields.integrationFailedTitle.label'),
        value: t('pages.settings.merchantForm.text.orderTextsFields.integrationFailedTitle.value'),
        type: 'text',
        rules: 'required'
      },
      integrationFailedSubtitle: {
        label: t('pages.settings.merchantForm.text.orderTextsFields.integrationFailedSubtitle.label'),
        value: t('pages.settings.merchantForm.text.orderTextsFields.integrationFailedSubtitle.value'),
        type: 'text',
        rules: 'required'
      }
    },
    rewardsTextsFields: {
      rewardsTitle: {
        label: t('pages.settings.merchantForm.text.rewardsTextsFields.rewardsTitle.label'),
        value: t('pages.settings.merchantForm.text.rewardsTextsFields.rewardsTitle.value'),
        type: 'text',
        rules: 'required'
      },
      rewardsSubtitle: {
        label: t('pages.settings.merchantForm.text.rewardsTextsFields.rewardsSubtitle.label'),
        value: t('pages.settings.merchantForm.text.rewardsTextsFields.rewardsSubtitle.value'),
        type: 'text',
        rules: 'required'
      },
      rewardsSubtitleNew: {
        label: t('pages.settings.merchantForm.text.rewardsTextsFields.rewardsSubtitleNew.label'),
        value: t('pages.settings.merchantForm.text.rewardsTextsFields.rewardsSubtitleNew.value'),
        type: 'text',
        rules: 'required'
      },
      rewardsText: {
        label: t('pages.settings.merchantForm.text.rewardsTextsFields.rewardsText.label'),
        value: t('pages.settings.merchantForm.text.rewardsTextsFields.rewardsText.value'),
        type: 'text',
        rules: 'required'
      },
      rewardsButton: {
        label: t('pages.settings.merchantForm.text.rewardsTextsFields.rewardsButton.label'),
        value: t('pages.settings.merchantForm.text.rewardsTextsFields.rewardsButton.value'),
        type: 'text',
        rules: 'required'
      },
      rewardsPopupConfirmTitle: {
        label: t('pages.settings.merchantForm.text.rewardsTextsFields.rewardsPopupConfirmTitle.label'),
        value: t('pages.settings.merchantForm.text.rewardsTextsFields.rewardsPopupConfirmTitle.value'),
        type: 'text',
        rules: 'required'
      },
      rewardsPopupConfirmSubtitle: {
        label: t('pages.settings.merchantForm.text.rewardsTextsFields.rewardsPopupConfirmSubtitle.label'),
        value: `${t(
          'pages.settings.merchantForm.text.rewardsTextsFields.rewardsPopupConfirmSubtitle.value1'
        )} {{points}} ${t(
          'pages.settings.merchantForm.text.rewardsTextsFields.rewardsPopupConfirmSubtitle.value2'
        )} {{reward}} ?`,
        type: 'text',
        rules: 'required'
      },
      rewardsPopupForbiddenTitle: {
        label: t('pages.settings.merchantForm.text.rewardsTextsFields.rewardsPopupForbiddenTitle.label'),
        value: t('pages.settings.merchantForm.text.rewardsTextsFields.rewardsPopupForbiddenTitle.value'),
        type: 'text',
        rules: 'required'
      }
    },
    rewardWonTextsFields: {
      congratsTitle: {
        label: t('pages.settings.merchantForm.text.rewardWonTextsFields.congratsTitle.label'),
        value: t('pages.settings.merchantForm.text.rewardWonTextsFields.congratsTitle.value'),
        type: 'text',
        rules: 'required'
      },
      congratsSubtitle: {
        label: t('pages.settings.merchantForm.text.rewardWonTextsFields.congratsSubtitle.label'),
        value: t('pages.settings.merchantForm.text.rewardWonTextsFields.congratsSubtitle.value'),
        type: 'text',
        rules: 'required'
      },
      congratsSubtitleSms: {
        label: t('pages.settings.merchantForm.text.rewardWonTextsFields.congratsSubtitleSms.label'),
        value: t('pages.settings.merchantForm.text.rewardWonTextsFields.congratsSubtitleSms.value'),
        type: 'text',
        rules: 'required'
      }
    },
    orderReceivedTextsFields: {
      integrationAfterOrderMailSubject: {
        label: t('pages.settings.merchantForm.text.orderReceivedTextsFields.integrationAfterOrderMailSubject.label'),
        value: '',
        type: 'text',
        rules: 'required'
      },
      integrationAfterOrderMailBody: {
        label: t('pages.settings.merchantForm.text.integrationAfterOrderMailBody.integrationAfterOrderMailBody.label'),
        value: '',
        type: 'text',
        rules: 'required'
      },
      integrationAfterOrderSms: {
        label: t('pages.settings.merchantForm.text.orderReceivedTextsFields.integrationAfterOrderSms.label'),
        value: '',
        type: 'text',
        rules: 'required'
      }
    },
    questionsTextsFields: {
      questionsOptin: {
        label: t('pages.settings.merchantForm.text.questionsTextsFields.questionsOptin.label'),
        value: t('pages.settings.merchantForm.text.questionsTextsFields.questionsOptin.value'),
        type: 'text',
        rules: 'required'
      },
      questionsOptinYes: {
        label: t('pages.settings.merchantForm.text.questionsTextsFields.questionsOptinYes.label'),
        value: t('pages.settings.merchantForm.text.questionsTextsFields.questionsOptinYes.value'),
        type: 'text',
        rules: 'required'
      },
      questionsOptinNo: {
        label: t('pages.settings.merchantForm.text.questionsTextsFields.questionsOptinNo.label'),
        value: t('pages.settings.merchantForm.text.questionsTextsFields.questionsOptinNo.value'),
        type: 'text',
        rules: 'required'
      },
      questionsOptinEmail: {
        label: t('pages.settings.merchantForm.text.questionsTextsFields.questionsOptinEmail.label'),
        value: t('pages.settings.merchantForm.text.questionsTextsFields.questionsOptinEmail.value'),
        type: 'text',
        rules: 'required'
      },
      questionsIntro: {
        label: t('pages.settings.merchantForm.text.questionsTextsFields.questionsIntro.label'),
        value: t('pages.settings.merchantForm.text.questionsTextsFields.questionsIntro.value'),
        type: 'text',
        rules: 'required'
      },
      questionsIntroNew: {
        label: t('pages.settings.merchantForm.text.questionsTextsFields.questionsIntroNew.label'),
        value: t('pages.settings.merchantForm.text.questionsTextsFields.questionsIntroNew.value'),
        type: 'text',
        rules: 'required'
      },
      enrichmentQuestionBirthday: {
        label: t('pages.settings.merchantForm.text.questionsTextsFields.enrichmentQuestionBirthday.label'),
        value: t('pages.settings.merchantForm.text.questionsTextsFields.enrichmentQuestionBirthday.value'),
        type: 'text',
        rules: 'required'
      },
      enrichmentQuestionFirstname: {
        label: t('pages.settings.merchantForm.text.questionsTextsFields.enrichmentQuestionFirstname.label'),
        value: t('pages.settings.merchantForm.text.questionsTextsFields.enrichmentQuestionFirstname.value'),
        type: 'text',
        rules: 'required'
      },
      enrichmentQuestionGender: {
        label: t('pages.settings.merchantForm.text.questionsTextsFields.enrichmentQuestionGender.label'),
        value: t('pages.settings.merchantForm.text.questionsTextsFields.enrichmentQuestionGender.value'),
        type: 'text',
        rules: 'required'
      },
      enrichmentQuestionEmail: {
        label: t('pages.settings.merchantForm.text.questionsTextsFields.enrichmentQuestionEmail.label'),
        value: t('pages.settings.merchantForm.text.questionsTextsFields.enrichmentQuestionEmail.value'),
        type: 'text',
        rules: 'required'
      },
      questionsAnswered: {
        label: t('pages.settings.merchantForm.text.questionsTextsFields.questionsAnswered.label'),
        value: t('pages.settings.merchantForm.text.questionsTextsFields.questionsAnswered.value'),
        type: 'text',
        rules: 'required'
      },
      questionsNotAnswered: {
        label: t('pages.settings.merchantForm.text.questionsTextsFields.questionsNotAnswered.label'),
        value: t('pages.settings.merchantForm.text.questionsTextsFields.questionsNotAnswered.value'),
        type: 'text',
        rules: 'required'
      }
    },
    uniqueRewardTexts: {
      congratsSms: {
        label: t('pages.settings.merchantForm.text.rewardWonTextsFields.congratsSms.label'),
        value: t('pages.settings.merchantForm.text.rewardWonTextsFields.congratsSms.value'),
        type: 'text',
        rules: 'required'
      },
      voucherSms: {
        label: t('pages.settings.merchantForm.text.rewardWonTextsFields.voucherSms.label'),
        value: t('pages.settings.merchantForm.text.rewardWonTextsFields.voucherSms.value'),
        type: 'text',
        rules: 'required'
      },
      congratsSmsNew: {
        label: t('pages.settings.merchantForm.text.rewardWonTextsFields.congratsSmsNew.label'),
        value: t('pages.settings.merchantForm.text.rewardWonTextsFields.congratsSmsNew.value'),
        type: 'text',
        rules: 'required'
      }
    },
    languageTone: {
      usePoliteLanguage: {
        value: false
      }
    },
    webappTextsFields: createWebappTextsFields(false),
    webappRegistrationTextsFields: createWebappRegistrationTextsFields(false)
  }

  if (!merchantTextsForm.value) {
    merchantTextsForm.value = nonFormalDefaultTexts
  }

  // Placeholders follow the merchant's tone; values are the saved custom texts (empty = webapp default)
  const fillWebappRegistrationTexts = (merchantTexts: MerchantTexts) => {
    const fields = createWebappRegistrationTextsFields(merchantTexts.usePoliteLanguage)

    for (const [key, field] of Object.entries(fields)) {
      field.value = merchantTexts[key as keyof typeof fields] || field.defaultValue
    }

    merchantTextsForm.value.webappRegistrationTextsFields = fields
  }

  const prepareUpdatePayload = (obj: LoyaltyMerchantFormTexts) => {
    const payload = {}
    const isObject = (obj: any) => obj && typeof obj === 'object' && !Array.isArray(obj)
    const getMerchantConfigKey = (key: string) => key.split('.').pop()

    const setPayloadKeyValue = (key: string, value: any) => {
      const merchantConfigKey = getMerchantConfigKey(key)
      payload[merchantConfigKey] = value
    }

    for (const [key, value] of Object.entries(obj)) {
      if (isObject(value)) {
        if (Object.prototype.hasOwnProperty.call(value, 'value')) {
          const isDefaultValue = value.defaultValue !== undefined && value.value?.trim() === value.defaultValue
          setPayloadKeyValue(key, isDefaultValue ? '' : value.value)
        } else {
          const nestedObjectFlattened = prepareUpdatePayload(value)
          for (const [subKey, subValue] of Object.entries(nestedObjectFlattened)) {
            setPayloadKeyValue(subKey, subValue)
          }
        }
      } else {
        setPayloadKeyValue(key, value)
      }
    }
    return payload as MerchantTexts
  }

  const patchMerchantTextsFromTabletForm = async (withNotification = true) => {
    await patchMerchantTexts(prepareUpdatePayload(merchantTextsForm.value), withNotification)
  }

  // Only the texts edited on the webapp page: there, the other form groups still hold generic defaults
  // (not the merchant's saved texts), so sending them would overwrite the tablet texts.
  // The tone is always sent: the API resets it to informal when it is missing from a patch.
  const patchWebappTexts = async (only?: string[]) => {
    const { languageTone, webappTextsFields, webappRegistrationTextsFields } = merchantTextsForm.value
    const payload = prepareUpdatePayload({
      languageTone,
      webappTextsFields,
      webappRegistrationTextsFields
    } as LoyaltyMerchantFormTexts)
    await axios.patch(
      `/merchants/${user.value.merchant.id}/texts`,
      only
        ? Object.fromEntries(
            Object.entries(payload).filter(([key]) => key === 'usePoliteLanguage' || only.includes(key))
          )
        : payload
    )
  }

  const patchMerchantTexts = async (merchantTexts: MerchantTexts, withNotification = true) => {
    try {
      await axios.patch(`/merchants/${user.value.merchant.id}/texts`, merchantTexts)
      if (withNotification) {
        notification.create('success', {
          text: t('pages.settings.merchantForm.notifications.updateMerchantTexts.success'),
          timeout: 3000
        })
      }
    } catch {
      if (withNotification) {
        notification.create('error', {
          text: t('pages.settings.merchantForm.notifications.updateMerchantTexts.error'),
          timeout: 3000
        })
      }
    }
  }

  return {
    fillWebappRegistrationTexts,
    getMerchantTexts,
    merchantTextsForm,
    patchMerchantTextsFromTabletForm,
    patchWebappTexts,
    formalDefaultTexts,
    nonFormalDefaultTexts,
    patchMerchantTexts,
    isPolite,
    webappTextDefaults
  }
}
